import { Injectable, inject, signal } from '@angular/core';
import { BehaviorSubject, Subject, Subscription, timer } from 'rxjs';
import { SiaLayoutApi } from '../apis/sia-layout.api';
import { DrawerPlacement } from '../interfaces/drawer-configuration';
import { SiaLayout } from '../interfaces/sia-layout';
import { SiaSignalrMessage } from '../interfaces/sia-signalr-message';
import { SiaCacheDatabaseService } from './cache-database.service';
import { SiaSignalrService } from './signalr.service';

@Injectable({
  providedIn: 'root',
})
export class SiaLayoutService {
  private api = inject(SiaLayoutApi);
  private cacheDatabaseService = inject(SiaCacheDatabaseService);
  private signalrService = inject(SiaSignalrService);

  drawerPlacement = signal<DrawerPlacement>('right');

  private refreshTimer = timer(0, 5000);
  private refreshSub: Subscription = new Subscription();
  private timerWorking: boolean = false;
  private viewLayoutPopupSubject: Subject<{
    componentId: string;
    show: boolean;
  }> = new Subject<{ componentId: string; show: boolean }>();
  private panelLayoutShownSubject: Subject<string> = new Subject<string>();
  private allLayoutUpdatedSubject: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(false);
  private additionalTitleSubject: BehaviorSubject<{
    [guid: string]: string | undefined;
  }> = new BehaviorSubject<{ [guid: string]: string | undefined }>({});

  // Department change overlay
  private departmentChangeOverlaySubject: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(false);

  get viewLayoutPopup$() {
    return this.viewLayoutPopupSubject.asObservable();
  }

  get departmentChangeOverlay$() {
    return this.departmentChangeOverlaySubject.asObservable();
  }
  get allLayoutUpdated$() {
    return this.allLayoutUpdatedSubject.asObservable();
  }
  get panelLayoutShown$() {
    return this.panelLayoutShownSubject.asObservable();
  }

  private sidebarOpenSubject: Subject<boolean> = new Subject<boolean>();
  get sidebarOpen$() {
    return this.sidebarOpenSubject.asObservable();
  }
  private toggleSidebarSubject: Subject<void> = new Subject<void>();
  get toggleSidebar$() {
    return this.toggleSidebarSubject.asObservable();
  }

  /** Inserted by Angular inject() migration for backwards compatibility */

  constructor() {
    this.signalrService.serviceMessage$.subscribe((message) => {
      this.managSiaSignalrMessage(message);
    });
  }

  //#region private methods
  private managSiaSignalrMessage(message: SiaSignalrMessage) {
    if (!message || !message.messageTypeId) return;

    if (message.recipientId != 'all') return;

    if (
      message.messageTypeId == 'refresh-layouts' ||
      message.messageTypeId == 'signalr-reconnected'
    ) {
      this.manageRefreshLayoutsMessage(message);
    }
  }

  private manageRefreshLayoutsMessage(message: SiaSignalrMessage) {
    this.cacheDatabaseService.clearAllLayouts().then((done: any) => {
      this.allLayoutUpdatedSubject.next(done);
    });
  }

  private stopLayoutTimer() {
    this.timerWorking = false;
    if (this.refreshSub) {
      this.refreshSub.unsubscribe();
    }
  }

  private async updateLayoutsOnTimer() {
    const done: any = await this.cacheDatabaseService.layoutsLoaded();
    if (done) {
      return done;
    } else {
      const layouts = await this.api.getLayouts();
      await this.cacheDatabaseService.setLayouts(layouts);
      return true;
    }
  }
  //#endregion

  //#region public methods

  startLayoutTimer() {
    if (this.timerWorking) {
      return;
    }
    this.timerWorking = true;
    if (this.refreshSub) {
      this.refreshSub.unsubscribe();
    }
    this.refreshSub = this.refreshTimer.subscribe(async () => {
      const done: any = await this.updateLayoutsOnTimer();
      if (done) {
        this.allLayoutUpdatedSubject.next(done);
        this.stopLayoutTimer();
      }
    });
  }

  // sidebarMenuChanged(menuId: string) {
  //   this._sidebarMenuChanged_allLayoutUpdated.next(menuId);
  // }

  async updateLayout(l: SiaLayout) {
    const layout = await this.api.updateLayout(l);
    if (layout) {
      await this.cacheDatabaseService.updateLayout(layout);
      return this.updateLayouts();
    } else {
      return false;
    }
  }

  async updateLayouts(): Promise<any> {
    const done: any = await this.cacheDatabaseService.clearAllLayouts();
    this.allLayoutUpdatedSubject.next(done);
    return done;
  }

  // OK CACHE
  async getLayout(layoutId: string): Promise<SiaLayout> {
    let layout: SiaLayout = await this.cacheDatabaseService.getLayout(layoutId);
    if (layout) {
      return layout;
    } else {
      layout = await this.api.getLayout(layoutId);
      if (layout) {
        await this.cacheDatabaseService.setLayout(layout);
        return layout;
      } else {
        return layout;
      }
    }
  }
  getLayouts(): Promise<SiaLayout[]> {
    return this.cacheDatabaseService.getLayouts();
  }

  async getAllLayouts(): Promise<SiaLayout[]> {
    const layouts = await this.api.getLayouts();
    const done: any = await this.cacheDatabaseService.clearAllLayouts();
    await this.cacheDatabaseService.setLayouts(layouts);
    return layouts;
  }

  viewPopup(componentId: string) {
    this.viewLayoutPopupSubject.next({ componentId: componentId, show: true });
  }

  closePopUp(componentId: string) {
    this.viewLayoutPopupSubject.next({ componentId: componentId, show: false });
  }

  showAdditionalTitle(title: string | undefined, guid: string) {
    const key = guid ?? '';
    if (!key) {
      return;
    }

    this.additionalTitleSubject.next({
      ...this.additionalTitleSubject.getValue(),
      [key]: title,
    });
  }

  additionalTitle$() {
    return this.additionalTitleSubject.asObservable();
  }

  getAdditionalTitle(guid: string): string | undefined {
    return this.additionalTitleSubject.getValue()[guid];
  }

  showPanelLayout(panelId: string) {
    this.panelLayoutShownSubject.next(panelId);
  }

  // Department change overlay methods
  showDepartmentChangeOverlay() {
    this.departmentChangeOverlaySubject.next(true);
  }

  hideDepartmentChangeOverlay() {
    this.departmentChangeOverlaySubject.next(false);
  }

  //#endregion

  setSidebarStatuts(value: boolean) {
    this.sidebarOpenSubject.next(value);
  }
  toggleSidebar() {
    this.toggleSidebarSubject.next();
  }
}
